import React, { useState, useEffect, useRef } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { socket } from '../lib/socket';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Clock,
  CheckCircle, Zap, Target, Flame, Trophy, Star, Activity, 
  Sparkles, Crown, Rocket, ChevronRight, Sword, Shield, Gem,
  ArrowUpRight, ArrowDownRight, RefreshCw, Gamepad2,
  Award, Coins, Swords, Bell, AlertTriangle, Check, X, BarChart3, Wallet,
  Pencil, Trash2, HeartPulse, Skull, ShieldAlert, UserCheck
} from 'lucide-react';
import { formatCurrency, formatRelativeTime } from '../lib/utils';
import { usePageFilter } from '../contexts/PageFilterContext';
import { PageFilterTabs } from '../components/shared/PageFilterTabs';

// Particle Background
function ParticleBackground() {
  return (
    <div className="particles">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="particle"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 15}s`,
            animationDuration: `${15 + Math.random() * 10}s`,
            background: ['#00F5FF', '#9945FF', '#FF00E5', '#FFD700'][Math.floor(Math.random() * 4)]
          }}
        />
      ))}
    </div>
  );
}

// Animated Number
function AnimatedNumber({ value, prefix = '', suffix = '' }) {
  const [displayValue, setDisplayValue] = useState(0);
  const prevValue = useRef(0);
  
  useEffect(() => {
    const start = prevValue.current;
    const end = parseFloat(value) || 0;
    const duration = 1500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(start + (end - start) * eased);
      if (progress < 1) requestAnimationFrame(animate);
      else prevValue.current = end;
    };
    requestAnimationFrame(animate);
  }, [value]);
  
  return <span className="font-mono tabular-nums">{prefix}{Math.floor(displayValue).toLocaleString()}{suffix}</span>;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedPageId: selectedPage, branchPages, fetchBranchPages, dateRange } = usePageFilter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState(null);
  const [trendStats, setTrendStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerHealth, setPlayerHealth] = useState(null);
  const [healthPage, setHealthPage] = useState(1);
  const [healthFilter, setHealthFilter] = useState(null);
  
  // Celebration state for approval animations
  const [celebration, setCelebration] = useState(null);
  
  // Ping employees state
  const [showPingModal, setShowPingModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [pingMessage, setPingMessage] = useState('');
  
  // Wallet selection for approval
  const [wallets, setWallets] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalTransaction, setApprovalTransaction] = useState(null);
  const [selectedWalletForApproval, setSelectedWalletForApproval] = useState('');
  
  // Withdrawal approval state
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [showWithdrawalApprovalModal, setShowWithdrawalApprovalModal] = useState(false);
  const [withdrawalToApprove, setWithdrawalToApprove] = useState(null);
  const [selectedWithdrawWallet, setSelectedWithdrawWallet] = useState('');
  const [approvingAll, setApprovingAll] = useState(false);

  // Recent transactions panel state
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [editTxn, setEditTxn] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPlayerSearch, setEditPlayerSearch] = useState('');
  const [deleteTxn, setDeleteTxn] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  // Socket listener — join this admin's PRIVATE tenant room only
  useEffect(() => {
    if (user?.id) {
      socket.emit('join_room', { room: `admin_${user.id}` });
    }
    const handleNewPending = () => {
      new Audio('/sounds/notify.wav').play().catch(() => {});
      fetchDashboardData();
      fetchPendingWithdrawals();
    };
    socket.on('new_pending_transaction', handleNewPending);
    return () => { socket.off('new_pending_transaction', handleNewPending); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  useAutoRefresh(() => {
    fetchDashboardData();
    fetchPendingWithdrawals();
  }, 15000);

  useEffect(() => {
    fetchBranchPages();
    fetchDashboardData();
    fetchEmployees();
    fetchWallets();
    fetchPendingWithdrawals();
    fetchRecentTransactions();
    fetchAllPlayers();
    fetchPlayerHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage, dateRange]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get('/users?role=employee');
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };
  
  const fetchWallets = async () => {
    try {
      const res = await axios.get('/wallets');
      setWallets(res.data.wallets || []);
    } catch (error) {
      console.error('Failed to fetch wallets');
    }
  };

  const fetchPendingWithdrawals = async () => {
    try {
      const res = await axios.get('/withdrawals/pending');
      setPendingWithdrawals(res.data.withdrawals || []);
    } catch (error) {
      console.error('Failed to fetch pending withdrawals');
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const params = selectedPage ? `?page_id=${selectedPage}&limit=20` : '?limit=20';
      const res = await axios.get(`/transactions/recent${params}`);
      setRecentTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch recent transactions');
    }
  };

  const fetchAllPlayers = async () => {
    try {
      const res = await axios.get('/players');
      setAllPlayers(Array.isArray(res.data) ? res.data : res.data.players || []);
    } catch (error) {
      console.error('Failed to fetch players');
    }
  };

  const fetchPlayerHealth = async () => {
    try {
      const params = selectedPage ? `?page_id=${selectedPage}` : '';
      const res = await axios.get(`/player-health${params}`);
      setPlayerHealth(res.data);
    } catch (error) {
      console.error('Failed to fetch player health');
    }
  };

  const openEditModal = (txn) => {
    setEditTxn(txn);
    setEditForm({
      player_name: txn.player_name || '',
      player_id: txn.player_id || '',
      game_name: txn.game_name || '',
      deposit_amount: txn.deposit_amount || 0,
      wallet_id: txn.wallet_id || '',
      employee_name: txn.employee_name || '',
    });
    setEditPlayerSearch('');
  };

  const handleEditSave = async () => {
    if (!editTxn) return;
    try {
      await axios.put(`/transactions/${editTxn.id}`, editForm);
      toast.success('Transaction updated');
      setEditTxn(null);
      fetchRecentTransactions();
      fetchDashboardData();
      fetchWallets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTxn) return;
    try {
      await axios.delete(`/transactions/${deleteTxn.id}`);
      toast.success('Transaction deleted');
      setDeleteTxn(null);
      fetchRecentTransactions();
      fetchDashboardData();
      fetchWallets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const editFilteredPlayers = allPlayers.filter(p =>
    editPlayerSearch && p.name?.toLowerCase().includes(editPlayerSearch.toLowerCase())
  ).slice(0, 5);

  const fetchDashboardData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedPage) params.append('page_id', selectedPage);
      if (dateRange.startDate) params.append('start_date', dateRange.startDate);
      if (dateRange.endDate) params.append('end_date', dateRange.endDate);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const pendingParams = new URLSearchParams({ status: 'pending', limit: '20' });
      if (selectedPage) pendingParams.append('page_id', selectedPage);
      if (dateRange.startDate) pendingParams.append('start_date', dateRange.startDate);
      if (dateRange.endDate) pendingParams.append('end_date', dateRange.endDate);
      const statsParams = new URLSearchParams();
      if (selectedPage) statsParams.append('page_id', selectedPage);
      if (dateRange.startDate) statsParams.append('start_date', dateRange.startDate);
      if (dateRange.endDate) statsParams.append('end_date', dateRange.endDate);
      const statsQs = statsParams.toString() ? `?${statsParams.toString()}` : '';
      const [statsRes, dashRes, pendingRes, transStatsRes, trendRes] = await Promise.all([
        axios.get(`/reports/dashboard${qs}`),
        axios.get(`/dashboard-stats${qs}`),
        axios.get(`/transactions?${pendingParams.toString()}`),
        axios.get(`/transactions/stats${statsQs}`),
        axios.get(`/dashboard-trends${selectedPage ? '?page_id=' + selectedPage : ''}`)
      ]);

      setStats(statsRes.data);
      setDashboardStats(dashRes.data);
      setPendingTransactions(pendingRes.data);
      setTransactionStats(transStatsRes.data);
      setTrendStats(trendRes.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transactionId, playerName, amount, walletId = null) => {
    try {
      const params = walletId ? `?wallet_id=${walletId}` : '';
      await axios.patch(`/transactions/${transactionId}/approve${params}`);
      
      // Show celebration animation
      setCelebration({ playerName, amount });
      
      // Clear celebration after 4 seconds
      setTimeout(() => setCelebration(null), 4000);
      
      // Close approval modal if open
      setShowApprovalModal(false);
      setApprovalTransaction(null);
      setSelectedWalletForApproval('');
      
      fetchDashboardData();
      fetchWallets();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };
  
  const openApprovalModal = (trans) => {
    setApprovalTransaction(trans);
    // Auto-match wallet from employee's payment_source
    const ps = trans.payment_source || '';
    const matched = wallets.find(w => {
      const label = `${w.wallet_name}${w.tag_name ? ' - ' + w.tag_name : ''}`;
      return ps === label || ps === w.wallet_name;
    });
    setSelectedWalletForApproval(matched ? matched.id : '');
    setShowApprovalModal(true);
  };
  
  const handleQuickApprove = (trans) => {
    // If wallets exist, show modal to select wallet
    if (wallets.length > 0) {
      openApprovalModal(trans);
    } else {
      // No wallets, approve directly
      handleApprove(trans.id, trans.player_name, trans.deposit_amount);
    }
  };

  const handleApproveAll = async () => {
    if (approvingAll) return;
    setApprovingAll(true);
    try {
      const res = await axios.post('/transactions/approve-all');
      const count = res.data.approved_count || 0;
      if (count > 0) {
        toast.success(`Approved ${count} transaction${count > 1 ? 's' : ''}!`);
        setCelebration({ playerName: 'ALL', amount: pendingTransactions.reduce((s, t) => s + (t.deposit_amount || 0), 0) });
        setTimeout(() => setCelebration(null), 4000);
      } else {
        toast.info('No pending transactions to approve');
      }
      fetchDashboardData();
      fetchWallets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve all');
    } finally {
      setApprovingAll(false);
    }
  };

  // Withdrawal approval handlers
  const openWithdrawalApprovalModal = (withdrawal) => {
    setWithdrawalToApprove(withdrawal);
    setSelectedWithdrawWallet('');
    setShowWithdrawalApprovalModal(true);
  };

  const handleApproveWithdrawal = async () => {
    if (!withdrawalToApprove) return;
    try {
      const params = selectedWithdrawWallet ? `?wallet_id=${selectedWithdrawWallet}` : '';
      await axios.patch(`/withdrawals/${withdrawalToApprove.id}/approve${params}`);
      toast.success(`Withdrawal approved for ${withdrawalToApprove.player_name}`);
      setShowWithdrawalApprovalModal(false);
      setWithdrawalToApprove(null);
      setSelectedWithdrawWallet('');
      fetchPendingWithdrawals();
      fetchWallets();
    } catch (error) {
      toast.error('Failed to approve withdrawal');
    }
  };

  const handleRejectWithdrawal = async (withdrawalId) => {
    try {
      await axios.patch(`/withdrawals/${withdrawalId}/reject`);
      toast.success('Withdrawal rejected');
      fetchPendingWithdrawals();
    } catch (error) {
      toast.error('Failed to reject withdrawal');
    }
  };

  const handlePingEmployees = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Select at least one employee to ping');
      return;
    }
    
    try {
      await axios.post('/ping-employees', {
        employee_ids: selectedEmployees,
        message: pingMessage || 'ALERT! Admin needs your attention!'
      });
      
      toast.success(`Pinged ${selectedEmployees.length} employee(s)!`);
      setShowPingModal(false);
      setSelectedEmployees([]);
      setPingMessage('');
    } catch (error) {
      toast.error('Failed to send ping');
    }
  };

  const toggleEmployeeSelection = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#080810]">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-[#9945FF]/30 border-t-[#00F5FF]" />
          <Gamepad2 className="absolute inset-0 m-auto w-8 h-8 text-[#00F5FF]" />
        </motion.div>
      </div>
    );
  }

  const d = dashboardStats || {};
  const ts = transactionStats || {};

  return (
    <div className="min-h-screen bg-[#080810] overflow-hidden" data-testid="admin-dashboard">
      <ParticleBackground />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      <div className="fixed inset-0 grid-lines pointer-events-none" />
      <div className="fixed top-0 left-1/4 w-[600px] h-[600px] bg-[#9945FF]/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[600px] bg-[#00F5FF]/10 rounded-full blur-[150px] pointer-events-none" />

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Header with Page Selector */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <motion.div
                animate={{ boxShadow: ['0 0 20px #FFD700', '0 0 40px #FFD700', '0 0 20px #FFD700'] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="p-3 rounded-xl bg-gradient-to-br from-[#FFD700]/20 to-[#FF6B00]/20 border border-[#FFD700]/30"
              >
                <Crown className="w-8 h-8 text-[#FFD700]" />
              </motion.div>
              <div>
                <Badge className="bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/50 mb-1">
                  <Gamepad2 className="w-3 h-3 mr-1" /> COMMAND CENTER
                </Badge>
                <h1 className="text-3xl md:text-4xl font-black gradient-text">GAME ON, BOSS!</h1>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Ping Employees Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPingModal(true)}
                className="px-4 py-2.5 rounded-xl font-bold text-white flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #FF3366, #FF6B00)' }}
                data-testid="ping-employees-btn"
              >
                <Bell className="w-4 h-4" />
                PING ALL
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05, rotate: 180 }}
                whileTap={{ scale: 0.95 }}
                onClick={fetchDashboardData}
                className="p-2.5 rounded-xl bg-[#0d0d15] border border-white/10 hover:border-[#00F5FF]/50"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-5 h-5 text-[#00F5FF]" />
              </motion.button>
            </div>
          </div>
          <div className="mt-4">
            <PageFilterTabs />
          </div>
        </motion.div>

        {/* 🔴 PENDING BATTLES - TOP PRIORITY */}
        {pendingTransactions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-gradient-to-r from-[#FF3366]/10 to-[#FFD700]/10 border-2 border-[#FF3366]/50 relative overflow-hidden">
              <motion.div
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute inset-0 bg-gradient-to-r from-[#FF3366]/5 to-transparent"
              />
              
              <div className="relative p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      className="p-2 rounded-lg bg-[#FF3366]/20"
                    >
                      <AlertTriangle className="w-6 h-6 text-[#FF3366]" />
                    </motion.div>
                    <div>
                      <h2 className="text-xl font-black text-white flex items-center gap-2">
                        PENDING APPROVALS
                        <motion.span
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ repeat: Infinity, duration: 0.8 }}
                          className="px-3 py-1 rounded-full bg-[#FF3366] text-white text-sm"
                        >
                          {pendingTransactions.length}
                        </motion.span>
                      </h2>
                      <p className="text-sm text-gray-400">Quick approve deposits & withdrawals</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleApproveAll}
                      disabled={approvingAll}
                      className="px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 text-black"
                      style={{ background: 'linear-gradient(135deg, #00FF88, #00F5FF)' }}
                      data-testid="approve-all-btn"
                    >
                      {approvingAll ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      {approvingAll ? 'APPROVING...' : 'APPROVE ALL'}
                    </motion.button>
                    <Button
                      onClick={() => navigate('/transactions')}
                      variant="outline"
                      className="border-[#FF3366]/50 text-[#FF3366] hover:bg-[#FF3366]/10"
                    >
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {pendingTransactions.slice(0, 8).map((trans, idx) => (
                    <motion.div
                      key={trans.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="p-4 rounded-xl bg-[#0d0d15] border border-white/10 hover:border-[#FFD700]/50 relative overflow-hidden"
                    >
                      {/* Type indicator */}
                      <div className={`absolute top-0 left-0 w-full h-1 ${trans.transaction_type === 'deposit' ? 'bg-gradient-to-r from-[#00FF88] to-[#00F5FF]' : 'bg-gradient-to-r from-[#FF3366] to-[#FF6B00]'}`} />
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative">
                          <Avatar className="w-12 h-12 border-2 border-[#FFD700]">
                            <AvatarFallback className="text-sm bg-gradient-to-br from-[#9945FF] to-[#00F5FF] text-white font-bold">
                              {trans.player_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${trans.transaction_type === 'deposit' ? 'bg-[#00FF88]' : 'bg-[#FF3366]'}`}>
                            {trans.transaction_type === 'deposit' 
                              ? <TrendingUp className="w-3 h-3 text-black" />
                              : <TrendingDown className="w-3 h-3 text-white" />
                            }
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white truncate">{trans.player_name}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${trans.transaction_type === 'deposit' ? 'border-[#00FF88]/50 text-[#00FF88]' : 'border-[#FF3366]/50 text-[#FF3366]'}`}>
                              {trans.transaction_type?.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-gray-500">{trans.game_name}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Amount and Approve */}
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <div>
                          <p className="text-xs text-gray-500">Amount</p>
                          <p className="text-xl font-black text-[#FFD700]">{formatCurrency(trans.deposit_amount)}</p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleQuickApprove(trans)}
                          className="px-4 py-2 rounded-xl font-bold text-black flex items-center gap-2"
                          style={{ background: 'linear-gradient(135deg, #00FF88, #00F5FF)' }}
                          data-testid={`approve-${trans.id}`}
                        >
                          <Check className="w-4 h-4" />
                          APPROVE
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* PENDING WITHDRAWALS */}
        {pendingWithdrawals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-gradient-to-r from-[#FF6B00]/10 to-[#FF3366]/10 border-2 border-[#FF6B00]/50 relative overflow-hidden">
              <motion.div
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/5 to-transparent"
              />
              
              <div className="relative p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      className="p-2 rounded-lg bg-[#FF6B00]/20"
                    >
                      <TrendingDown className="w-6 h-6 text-[#FF6B00]" />
                    </motion.div>
                    <div>
                      <h2 className="text-xl font-black text-white flex items-center gap-2">
                        WITHDRAWAL REQUESTS
                        <motion.span
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ repeat: Infinity, duration: 0.8 }}
                          className="px-3 py-1 rounded-full bg-[#FF6B00] text-white text-sm"
                        >
                          {pendingWithdrawals.length}
                        </motion.span>
                      </h2>
                      <p className="text-sm text-gray-400">Approve or reject withdrawal requests</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {pendingWithdrawals.slice(0, 8).map((w, idx) => (
                    <motion.div
                      key={w.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="p-4 rounded-xl bg-[#0d0d15] border border-white/10 hover:border-[#FF6B00]/50 relative overflow-hidden"
                    >
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF3366] to-[#FF6B00]" />
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative">
                          <Avatar className="w-12 h-12 border-2 border-[#FF6B00]">
                            <AvatarFallback className="text-sm bg-gradient-to-br from-[#FF3366] to-[#FF6B00] text-white font-bold">
                              {w.player_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center bg-[#FF3366]">
                            <TrendingDown className="w-3 h-3 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white truncate">{w.player_name}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#FF6B00]/50 text-[#FF6B00]">
                              WITHDRAW
                            </Badge>
                            <span className="text-xs text-gray-500">{w.game_name}</span>
                          </div>
                        </div>
                      </div>

                      {w.win_amount > 0 && (
                        <div className="mb-2 text-xs text-gray-500">
                          Win: <span className="text-[#FFD700]">{formatCurrency(w.win_amount)}</span>
                          {w.player_tag && <span className="ml-2">Tag: {w.player_tag}</span>}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <div>
                          <p className="text-xs text-gray-500">Amount</p>
                          <p className="text-xl font-black text-[#FF3366]">{formatCurrency(w.deposit_amount)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRejectWithdrawal(w.id)}
                            className="p-2 rounded-lg bg-[#FF3366]/20 hover:bg-[#FF3366]/40"
                            data-testid={`reject-withdrawal-${w.id}`}
                          >
                            <X className="w-4 h-4 text-[#FF3366]" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => openWithdrawalApprovalModal(w)}
                            className="px-3 py-2 rounded-xl font-bold text-black flex items-center gap-1 text-sm"
                            style={{ background: 'linear-gradient(135deg, #00FF88, #00F5FF)' }}
                            data-testid={`approve-withdrawal-${w.id}`}
                          >
                            <Check className="w-4 h-4" />
                            APPROVE
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Today vs Yesterday Trend Stats - Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
          <Card className="bg-[#0d0d15] border-0 relative overflow-hidden" data-testid="trend-stats-table">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
            <div className="p-4 pb-2 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#00F5FF]" />
              <span className="text-xs font-bold text-gray-400 tracking-widest uppercase">Today vs Yesterday</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Metric</th>
                    <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Today</th>
                    <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Yesterday</th>
                    <th className="px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'total_revenue', label: 'Total Revenue', icon: DollarSign, color: '#FFD700', isCurrency: true },
                    { key: 'active_players', label: 'Active Players', icon: Users, color: '#9945FF', isCurrency: false },
                    { key: 'new_players', label: 'New Players', icon: Sparkles, color: '#00F5FF', isCurrency: false },
                    { key: 'deposits', label: 'Deposits', icon: TrendingUp, color: '#00FF88', isCurrency: true },
                    { key: 'withdrawals', label: 'Withdrawals', icon: TrendingDown, color: '#FF3366', isCurrency: true },
                    { key: 'net_profit', label: 'Net Profit', icon: Target, color: '#FF6B00', isCurrency: true },
                  ].map(({ key, label, icon: Icon, color, isCurrency }) => {
                    const data = trendStats?.[key] || { today: 0, yesterday: 0, trend: 0 };
                    const trendUp = data.trend > 0;
                    const trendZero = data.trend === 0;
                    const trendColor = trendZero ? '#6b7280' : trendUp ? '#00FF88' : '#FF3366';
                    const TrendArrow = trendUp ? ArrowUpRight : ArrowDownRight;
                    return (
                      <tr key={key} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`trend-row-${key}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                            <span className="text-sm font-semibold text-white">{label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-bold font-mono text-white">
                            {isCurrency ? formatCurrency(data.today) : data.today.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-mono text-gray-400">
                            {isCurrency ? formatCurrency(data.yesterday) : data.yesterday.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {trendZero ? (
                            <span className="text-sm text-gray-500">0%</span>
                          ) : (
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: `${trendColor}12` }}>
                              <TrendArrow className="w-3.5 h-3.5" style={{ color: trendColor }} />
                              <span className="text-xs font-bold" style={{ color: trendColor }}>
                                {trendUp ? '+' : ''}{data.trend}%
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Total Deposits */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-4 bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#00FF88]" />
                <span className="text-xs text-gray-400">DEPOSITS</span>
              </div>
              <p className="text-2xl font-black text-[#00FF88]">
                <AnimatedNumber value={ts.total_deposits || d.total_deposits || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>

          {/* Total Withdrawals */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="p-4 bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-[#FF3366]" />
                <span className="text-xs text-gray-400">WITHDRAWALS</span>
              </div>
              <p className="text-2xl font-black text-[#FF3366]">
                <AnimatedNumber value={ts.total_withdrawals || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>

          {/* 24H Volume */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="p-4 bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#FFD700]" />
                <span className="text-xs text-gray-400">24H VOLUME</span>
              </div>
              <p className="text-2xl font-black text-[#FFD700]">
                <AnimatedNumber value={ts.volume_24h || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>

          {/* Active Players */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="p-4 bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-[#9945FF]" />
                <span className="text-xs text-gray-400">WARRIORS</span>
              </div>
              <p className="text-2xl font-black text-[#9945FF]">
                <AnimatedNumber value={d.total_players || 0} />
              </p>
            </Card>
          </motion.div>

          {/* Pending Count */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="p-4 bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-[#00F5FF]" />
                <span className="text-xs text-gray-400">PENDING</span>
              </div>
              <p className="text-2xl font-black text-[#00F5FF]">
                <AnimatedNumber value={pendingTransactions.length} />
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* 7-Day Volume Chart */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
            <Card className="h-full bg-[#0d0d15] border-0 p-5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#00F5FF]" /> 7-DAY VOLUME
              </h3>
              <div className="space-y-3">
                {(ts.daily_data || []).map((day, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-8">{day.day_name}</span>
                    <div className="flex-1 h-6 bg-[#1a1a2e] rounded-full overflow-hidden relative">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((day.deposits / (ts.volume_7d || 1)) * 100 * 7, 100)}%` }}
                        transition={{ delay: 0.5 + idx * 0.1, duration: 0.8 }}
                        className="h-full bg-gradient-to-r from-[#00FF88] to-[#00F5FF] rounded-full"
                      />
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((day.withdrawals / (ts.volume_7d || 1)) * 100 * 7, 100)}%` }}
                        transition={{ delay: 0.6 + idx * 0.1, duration: 0.8 }}
                        className="absolute top-0 right-0 h-full bg-gradient-to-r from-[#FF3366] to-[#FF6B00] rounded-full"
                        style={{ opacity: 0.5 }}
                      />
                    </div>
                    <span className="text-xs text-[#00FF88] font-mono w-16 text-right">{formatCurrency(day.deposits)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Top Warriors */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="h-full bg-[#0d0d15] border-0 p-5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#FFD700]" /> TOP WARRIORS
              </h3>
              <div className="space-y-3">
                {(ts.top_depositors || []).map((player, idx) => {
                  const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];
                  return (
                    <motion.div
                      key={player.player_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + idx * 0.1 }}
                      className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                      onClick={() => navigate('/players')}
                    >
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
                        style={{ background: `${medals[idx]}20`, color: medals[idx] }}
                      >
                        #{idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white truncate">{player.player_name}</p>
                        <p className="text-xs text-gray-500">{player.transaction_count} battles</p>
                      </div>
                      <p className="font-mono font-bold text-[#FFD700]">{formatCurrency(player.total_deposits)}</p>
                    </motion.div>
                  );
                })}
                {(!ts.top_depositors || ts.top_depositors.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Trophy className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No warriors yet</p>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Recent Transactions Panel */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
            <Card className="h-full bg-[#0d0d15] border-0 relative overflow-hidden flex flex-col" data-testid="recent-transactions-panel">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
              <div className="p-5 pb-3 flex items-center justify-between">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Coins className="w-5 h-5 text-[#9945FF]" /> RECENT TRANSACTIONS
                </h3>
                <Badge className="bg-[#9945FF]/20 text-[#9945FF] border-[#9945FF]/50 text-[10px]">
                  {recentTransactions.length}
                </Badge>
              </div>
              <ScrollArea className="flex-1 px-5 pb-5" style={{ maxHeight: 380 }}>
                <div className="space-y-2">
                  {recentTransactions.map((txn) => {
                    const isDeposit = txn.transaction_type === 'deposit';
                    const walletObj = wallets.find(w => w.id === txn.wallet_id);
                    const walletLabel = walletObj ? `${walletObj.wallet_name}${walletObj.tag_name ? ' - ' + walletObj.tag_name : ''}` : (txn.payment_source || '—');
                    return (
                      <div
                        key={txn.id}
                        className="p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all group"
                        data-testid={`recent-txn-${txn.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isDeposit ? 'bg-[#00FF88]/10' : 'bg-[#FF3366]/10'}`}>
                            {isDeposit ? <TrendingUp className="w-4 h-4 text-[#00FF88]" /> : <TrendingDown className="w-4 h-4 text-[#FF3366]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-semibold text-white text-sm truncate">{txn.player_name}</p>
                              <p className={`font-mono font-bold text-sm ${isDeposit ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                                {isDeposit ? '+' : '-'}{formatCurrency(txn.deposit_amount)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-gray-500">by {txn.employee_name || '—'}</span>
                              <span className="text-[10px] text-gray-600">|</span>
                              <span className="text-[10px] text-gray-500 truncate">{walletLabel}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-gray-600">
                                {txn.created_at ? new Date(txn.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openEditModal(txn)}
                                  className="p-1 rounded-md hover:bg-[#9945FF]/20 transition-colors"
                                  data-testid={`edit-txn-${txn.id}`}
                                >
                                  <Pencil className="w-3 h-3 text-[#9945FF]" />
                                </button>
                                <button
                                  onClick={() => setDeleteTxn(txn)}
                                  className="p-1 rounded-md hover:bg-[#FF3366]/20 transition-colors"
                                  data-testid={`delete-txn-${txn.id}`}
                                >
                                  <Trash2 className="w-3 h-3 text-[#FF3366]" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {recentTransactions.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Coins className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No recent transactions</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </motion.div>

        </div>

        {/* Player Health & Reactivation Section */}
        {playerHealth && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="mb-6" data-testid="player-health-section">
            <Card className="bg-[#0d0d15] border-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
              <div className="p-5 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HeartPulse className="w-5 h-5 text-[#FF3366]" />
                  <span className="font-bold text-white">PLAYER HEALTH</span>
                </div>
              </div>

              {/* Summary Cards - Clickable */}
              <div className="px-5 grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Active', key: 'active', count: playerHealth.summary.active, color: '#00FF88', icon: UserCheck },
                  { label: 'At Risk', key: 'at_risk', count: playerHealth.summary.at_risk, color: '#FFD700', icon: ShieldAlert },
                  { label: 'Dead', key: 'dead', count: playerHealth.summary.dead, color: '#FF3366', icon: Skull },
                  { label: 'VIP', key: 'vip', count: playerHealth.summary.vip, color: '#9945FF', icon: Crown },
                ].map(({ label, key, count, color, icon: Icon }) => (
                  <button key={label} onClick={() => { setHealthFilter(healthFilter === key ? null : key); setHealthPage(1); }} className="p-3 rounded-xl text-left transition-all" style={{ background: healthFilter === key ? `${color}15` : `${color}08`, border: `1px solid ${healthFilter === key ? color : `${color}20`}`, boxShadow: healthFilter === key ? `0 0 12px ${color}20` : 'none' }} data-testid={`health-filter-${key}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4" style={{ color }} />
                      <span className="text-xs font-semibold text-gray-400 uppercase">{label}</span>
                    </div>
                    <p className="text-2xl font-black" style={{ color }}>{count}</p>
                    {healthFilter === key && <span className="text-[10px] text-gray-400">Click to clear</span>}
                  </button>
                ))}
              </div>

              {/* Employee Reactivation Targets */}
              {playerHealth.employee_targets.length > 0 && (
                <div className="px-5 mb-4">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Reactivation Targets</p>
                  <div className="flex flex-wrap gap-2">
                    {playerHealth.employee_targets.map((et) => (
                      <div key={et.employee_name} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FF3366]/5 border border-[#FF3366]/15" data-testid={`emp-target-${et.employee_name}`}>
                        <div>
                          <span className="text-xs font-semibold text-white">{et.employee_name}</span>
                          <span className="text-[10px] text-gray-500 ml-2">{et.dead_players} dead</span>
                        </div>
                        <Badge className="bg-[#FF3366]/20 text-[#FF3366] border-[#FF3366]/30 text-[10px]">
                          Target: {et.target_today}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Player Table */}
              {(() => {
                const ITEMS_PER_PAGE = 20;
                const filtered = healthFilter ? playerHealth.players.filter(p => p.status === healthFilter) : playerHealth.players;
                const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                const paginated = filtered.slice((healthPage - 1) * ITEMS_PER_PAGE, healthPage * ITEMS_PER_PAGE);
                return (
                  <>
                    {healthFilter && (
                      <div className="px-5 mb-2">
                        <span className="text-xs text-gray-400">Showing <span className="font-bold text-white">{filtered.length}</span> {healthFilter.replace('_', ' ')} players</span>
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-y border-white/5">
                            <th className="px-5 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Player</th>
                            <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Page</th>
                            <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Employee</th>
                            <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Total Deposits</th>
                            <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Last Deposit</th>
                            <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Inactive</th>
                            <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginated.map((p) => {
                            const statusConfig = {
                              active: { bg: '#00FF88', label: 'Active' },
                              at_risk: { bg: '#FFD700', label: 'At Risk' },
                              dead: { bg: '#FF3366', label: 'Dead' },
                              vip: { bg: '#9945FF', label: 'VIP' },
                            }[p.status] || { bg: '#6b7280', label: p.status };
                            return (
                              <tr
                                key={p.player_id}
                                className={`border-b border-white/[0.03] transition-colors ${p.highlight ? 'bg-[#FF3366]/[0.04]' : 'hover:bg-white/[0.02]'}`}
                                data-testid={`health-row-${p.player_id}`}
                              >
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-2">
                                    {p.highlight && <AlertTriangle className="w-3.5 h-3.5 text-[#FF3366] shrink-0" />}
                                    <span className={`text-sm font-semibold ${p.highlight ? 'text-[#FF3366]' : 'text-white'}`}>{p.player_name}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-sm text-gray-400">{p.page_name}</td>
                                <td className="px-3 py-3 text-sm text-gray-400">{p.employee_name || '—'}</td>
                                <td className="px-3 py-3 text-sm font-mono text-right text-gray-300">{formatCurrency(p.total_deposits)}</td>
                                <td className="px-3 py-3 text-sm text-right text-gray-400">
                                  {p.last_deposit_date ? new Date(p.last_deposit_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Never'}
                                </td>
                                <td className="px-3 py-3 text-sm text-right">
                                  <span className={`font-mono ${p.days_inactive != null && p.days_inactive >= 5 ? 'text-[#FF3366] font-bold' : 'text-gray-400'}`}>
                                    {p.days_inactive != null ? `${p.days_inactive}d` : '—'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 text-center">
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                                    style={{ background: `${statusConfig.bg}15`, color: statusConfig.bg, border: `1px solid ${statusConfig.bg}30` }}
                                  >
                                    {statusConfig.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="p-3 flex items-center justify-between" data-testid="health-pagination">
                        <span className="text-xs text-gray-500">Page {healthPage} of {totalPages} ({filtered.length} players)</span>
                        <div className="flex gap-2">
                          <button onClick={() => setHealthPage(Math.max(1, healthPage - 1))} disabled={healthPage === 1} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/5 text-gray-300 disabled:opacity-30 hover:bg-white/10 transition-colors" data-testid="health-prev-page">Prev</button>
                          <button onClick={() => setHealthPage(Math.min(totalPages, healthPage + 1))} disabled={healthPage === totalPages} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/5 text-gray-300 disabled:opacity-30 hover:bg-white/10 transition-colors" data-testid="health-next-page">Next</button>
                        </div>
                      </div>
                    )}
                    {filtered.length === 0 && (
                      <div className="p-5 text-center text-sm text-gray-500">No players in this category</div>
                    )}
                  </>
                );
              })()}
            </Card>
          </motion.div>
        )}

        {/* Quick Navigation */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Warriors', icon: Sword, color: '#9945FF', path: '/players' },
              { name: 'Treasury', icon: Coins, color: '#FFD700', path: '/wallet' },
              { name: 'Battle Log', icon: Swords, color: '#FF3366', path: '/transactions' },
              { name: 'Intel', icon: Activity, color: '#00F5FF', path: '/reports' },
            ].map((item, idx) => (
              <motion.button
                key={item.name}
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(item.path)}
                className="p-4 rounded-xl text-left transition-all"
                style={{ 
                  background: `linear-gradient(135deg, ${item.color}15, ${item.color}05)`,
                  border: `1px solid ${item.color}30`
                }}
                data-testid={`quick-nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-8 h-8 mb-2" style={{ color: item.color }} />
                <p className="font-bold text-white">{item.name}</p>
                <ChevronRight className="w-4 h-4 mt-1" style={{ color: item.color }} />
              </motion.button>
            ))}
          </div>
        </motion.div>
      </main>

      {/* Celebration Animation on Approve */}
      <AnimatePresence>
        {celebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
          >
            {/* Confetti explosion */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(80)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ 
                    x: '50vw', 
                    y: '50vh',
                    scale: 0,
                    rotate: 0
                  }}
                  animate={{ 
                    x: `${Math.random() * 100}vw`,
                    y: `${Math.random() * 100}vh`,
                    scale: [0, 1, 0.5],
                    rotate: Math.random() * 720 - 360
                  }}
                  transition={{ 
                    duration: 2 + Math.random(),
                    ease: 'easeOut'
                  }}
                  className="absolute w-4 h-4 rounded-full"
                  style={{ 
                    backgroundColor: ['#FFD700', '#00F5FF', '#FF3366', '#00FF88', '#9945FF', '#FF6B00'][Math.floor(Math.random() * 6)],
                    boxShadow: '0 0 10px currentColor'
                  }}
                />
              ))}
            </div>
            
            {/* Center celebration content */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', damping: 12 }}
              className="text-center"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0]
                }}
                transition={{ repeat: 2, duration: 0.5 }}
                className="w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(135deg, #FFD700, #FF6B00)',
                  boxShadow: '0 0 60px #FFD700, 0 0 100px #FF6B00'
                }}
              >
                <Trophy className="w-16 h-16 text-black" />
              </motion.div>
              
              <motion.h1
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-6xl font-black mb-4"
                style={{ 
                  background: 'linear-gradient(135deg, #FFD700, #FF6B00)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 40px rgba(255,215,0,0.5)'
                }}
              >
                APPROVED!
              </motion.h1>
              
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-3xl text-white font-bold mb-2"
              >
                {formatCurrency(celebration.amount)}
              </motion.p>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex items-center justify-center gap-2 text-gray-400"
              >
                <span>for</span>
                <span className="px-3 py-1 rounded-full bg-[#FFD700]/20 text-[#FFD700] font-bold">
                  {celebration.playerName}
                </span>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ping Employees Modal */}
      <AnimatePresence>
        {showPingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border border-[#FF3366]/30 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
                
                <div className="p-4 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-[#FF3366]/20">
                        <Bell className="w-6 h-6 text-[#FF3366]" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">PING EMPLOYEES</h2>
                        <p className="text-sm text-gray-400">Send alert notification with alarm</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setShowPingModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-4">
                  {/* Select All */}
                  <div className="flex items-center justify-between mb-3 p-2 rounded-lg bg-white/5">
                    <span className="text-sm text-gray-400">Select employees to ping</span>
                    <button
                      onClick={selectAllEmployees}
                      className="text-sm text-[#00F5FF] hover:underline"
                    >
                      {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {/* Employee List */}
                  <ScrollArea className="h-[200px] mb-4">
                    <div className="space-y-2">
                      {employees.map((emp) => (
                        <motion.div
                          key={emp.id}
                          whileHover={{ scale: 1.01 }}
                          onClick={() => toggleEmployeeSelection(emp.id)}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                            selectedEmployees.includes(emp.id) 
                              ? 'bg-[#FF3366]/20 border border-[#FF3366]/50' 
                              : 'bg-white/5 border border-transparent hover:border-white/10'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${
                            selectedEmployees.includes(emp.id) ? 'bg-[#FF3366]' : 'border border-white/20'
                          }`}>
                            {selectedEmployees.includes(emp.id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-gradient-to-br from-[#9945FF] to-[#00F5FF] text-white text-xs">
                              {emp.username?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium text-white">{emp.username}</p>
                            <p className="text-xs text-gray-500">{emp.email}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 block mb-2">Alert Message (optional)</label>
                    <input
                      type="text"
                      value={pingMessage}
                      onChange={(e) => setPingMessage(e.target.value)}
                      placeholder="ALERT! Check your dashboard!"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>

                  {/* Action */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handlePingEmployees}
                    disabled={selectedEmployees.length === 0}
                    className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: selectedEmployees.length > 0 ? 'linear-gradient(135deg, #FF3366, #FF6B00)' : '#333' }}
                    data-testid="send-ping-btn"
                  >
                    <Bell className="w-5 h-5" />
                    PING {selectedEmployees.length > 0 ? `${selectedEmployees.length} EMPLOYEE(S)` : 'SELECTED'}
                  </motion.button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Approval with Wallet Selection Modal */}
      <AnimatePresence>
        {showApprovalModal && approvalTransaction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowApprovalModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border border-[#00FF88]/30 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
                
                <div className="p-4 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-[#00FF88]/20">
                        <Check className="w-6 h-6 text-[#00FF88]" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">APPROVE DEPOSIT</h2>
                        <p className="text-sm text-gray-400">Select wallet to record this deposit</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setShowApprovalModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  {/* Transaction Details */}
                  <div className="p-4 rounded-xl bg-white/5">
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar className="w-12 h-12 border-2 border-[#FFD700]">
                        <AvatarFallback className="text-sm bg-gradient-to-br from-[#9945FF] to-[#00F5FF] text-white font-bold">
                          {approvalTransaction.player_name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-bold text-white">{approvalTransaction.player_name}</p>
                        <p className="text-sm text-gray-400">{approvalTransaction.payment_source || 'Unknown'}</p>
                      </div>
                    </div>
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-500">Amount</p>
                      <p className="text-3xl font-black text-[#FFD700]">{formatCurrency(approvalTransaction.deposit_amount)}</p>
                    </div>
                  </div>
                  
                  {/* Wallet Selection */}
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Record to Wallet (optional)</label>
                    <select
                      value={selectedWalletForApproval}
                      onChange={(e) => setSelectedWalletForApproval(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white"
                      data-testid="approval-wallet-select"
                    >
                      <option value="">No wallet (skip)</option>
                      {wallets.map(w => (
                        <option key={w.id} value={w.id}>
                          {w.wallet_name}{w.tag_name ? ` - ${w.tag_name}` : ''} (${(w.balance || 0).toLocaleString()})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">This will add the deposit amount to the selected wallet's balance</p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleApprove(
                        approvalTransaction.id, 
                        approvalTransaction.player_name, 
                        approvalTransaction.deposit_amount,
                        selectedWalletForApproval || null
                      )}
                      className="flex-1 py-3 rounded-xl font-bold text-black flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #00FF88, #00F5FF)' }}
                      data-testid="confirm-approval-btn"
                    >
                      <Check className="w-5 h-5" />
                      APPROVE
                    </motion.button>
                    <Button
                      variant="outline"
                      onClick={() => setShowApprovalModal(false)}
                      className="flex-1 border-white/10"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Withdrawal Approval with Wallet Selection Modal */}
      <AnimatePresence>
        {showWithdrawalApprovalModal && withdrawalToApprove && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowWithdrawalApprovalModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border border-[#FF6B00]/30 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF6B00] to-transparent" />
                
                <div className="p-4 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-[#FF6B00]/20">
                        <TrendingDown className="w-6 h-6 text-[#FF6B00]" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">APPROVE WITHDRAWAL</h2>
                        <p className="text-sm text-gray-400">Select withdraw wallet</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setShowWithdrawalApprovalModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  {/* Withdrawal Details */}
                  <div className="p-4 rounded-xl bg-white/5">
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar className="w-12 h-12 border-2 border-[#FF6B00]">
                        <AvatarFallback className="text-sm bg-gradient-to-br from-[#FF3366] to-[#FF6B00] text-white font-bold">
                          {withdrawalToApprove.player_name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-bold text-white">{withdrawalToApprove.player_name}</p>
                        <p className="text-sm text-gray-400">{withdrawalToApprove.game_name}</p>
                      </div>
                    </div>
                    {withdrawalToApprove.win_amount > 0 && (
                      <div className="flex items-center gap-4 mb-2 text-sm text-gray-400">
                        <span>Win: <span className="text-[#FFD700]">{formatCurrency(withdrawalToApprove.win_amount)}</span></span>
                        {withdrawalToApprove.player_tag && <span>Tag: {withdrawalToApprove.player_tag}</span>}
                      </div>
                    )}
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-500">Withdrawal Amount</p>
                      <p className="text-3xl font-black text-[#FF3366]">{formatCurrency(withdrawalToApprove.deposit_amount)}</p>
                    </div>
                  </div>
                  
                  {/* Wallet Selection */}
                  <div>
                    <label className="text-sm text-gray-400 block mb-2">Withdraw From Wallet</label>
                    <select
                      value={selectedWithdrawWallet}
                      onChange={(e) => setSelectedWithdrawWallet(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white"
                      data-testid="withdrawal-wallet-select"
                    >
                      <option value="">No wallet (skip)</option>
                      {wallets.map(w => (
                        <option key={w.id} value={w.id}>
                          {w.wallet_name}{w.tag_name ? ` - ${w.tag_name}` : ''} (${(w.balance || 0).toLocaleString()})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">This will deduct the amount from the selected wallet</p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleApproveWithdrawal}
                      className="flex-1 py-3 rounded-xl font-bold text-black flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #00FF88, #00F5FF)' }}
                      data-testid="confirm-withdrawal-approval-btn"
                    >
                      <Check className="w-5 h-5" />
                      APPROVE
                    </motion.button>
                    <Button
                      variant="outline"
                      onClick={() => setShowWithdrawalApprovalModal(false)}
                      className="flex-1 border-white/10"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Transaction Modal */}
      <AnimatePresence>
        {editTxn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setEditTxn(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg"
            >
              <Card className="bg-[#0d0d15] border border-[#9945FF]/30 overflow-hidden" data-testid="edit-transaction-modal">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-[#9945FF]/20">
                      <Pencil className="w-5 h-5 text-[#9945FF]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">EDIT TRANSACTION</h2>
                      <p className="text-xs text-gray-400">ID: {editTxn.id?.slice(0, 12)}...</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setEditTxn(null)}><X className="w-5 h-5" /></Button>
                </div>

                <div className="p-4 space-y-4">
                  {/* Player Name with autocomplete */}
                  <div className="relative">
                    <label className="text-xs text-gray-400 block mb-1">Player Name</label>
                    <input
                      type="text"
                      value={editForm.player_name}
                      onChange={(e) => {
                        setEditForm({ ...editForm, player_name: e.target.value, player_id: '' });
                        setEditPlayerSearch(e.target.value);
                      }}
                      className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
                      data-testid="edit-player-name"
                    />
                    {editPlayerSearch && !editForm.player_id && editFilteredPlayers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-[#1a1a2e] border border-white/10 rounded-xl overflow-hidden shadow-xl">
                        {editFilteredPlayers.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 transition-colors"
                            onClick={() => { setEditForm({ ...editForm, player_name: p.name, player_id: p.id }); setEditPlayerSearch(''); }}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {editForm.player_id && <p className="text-[10px] text-[#00FF88] mt-1">Linked to existing player</p>}
                    {editForm.player_name && !editForm.player_id && editPlayerSearch === '' && <p className="text-[10px] text-[#FFD700] mt-1">New player will be created</p>}
                  </div>

                  {/* Warrior (employee) Name */}
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Warrior Name</label>
                    <input
                      type="text"
                      value={editForm.employee_name}
                      onChange={(e) => setEditForm({ ...editForm, employee_name: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
                      data-testid="edit-employee-name"
                    />
                  </div>

                  {/* Amount + Wallet side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Deposit Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.deposit_amount}
                        onChange={(e) => setEditForm({ ...editForm, deposit_amount: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
                        data-testid="edit-deposit-amount"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Wallet</label>
                      <select
                        value={editForm.wallet_id}
                        onChange={(e) => setEditForm({ ...editForm, wallet_id: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
                        data-testid="edit-wallet"
                      >
                        <option value="">No wallet</option>
                        {wallets.map(w => (
                          <option key={w.id} value={w.id}>
                            {w.wallet_name}{w.tag_name ? ` - ${w.tag_name}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleEditSave}
                      className="flex-1 py-3 rounded-xl font-bold text-black flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #9945FF, #00F5FF)' }}
                      data-testid="save-edit-btn"
                    >
                      <Check className="w-5 h-5" /> SAVE CHANGES
                    </motion.button>
                    <Button variant="outline" onClick={() => setEditTxn(null)} className="flex-1 border-white/10">Cancel</Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteTxn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setDeleteTxn(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              <Card className="bg-[#0d0d15] border border-[#FF3366]/30 overflow-hidden" data-testid="delete-transaction-modal">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
                <div className="p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FF3366]/20 flex items-center justify-center">
                    <Trash2 className="w-7 h-7 text-[#FF3366]" />
                  </div>
                  <h2 className="text-lg font-bold text-white mb-2">DELETE TRANSACTION?</h2>
                  <p className="text-sm text-gray-400 mb-1">
                    {formatCurrency(deleteTxn.deposit_amount)} — {deleteTxn.player_name}
                  </p>
                  <p className="text-xs text-gray-500 mb-5">
                    This will update all player stats and wallet balances.
                  </p>
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDeleteConfirm}
                      className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 bg-[#FF3366]"
                      data-testid="confirm-delete-btn"
                    >
                      <Trash2 className="w-4 h-4" /> DELETE
                    </motion.button>
                    <Button variant="outline" onClick={() => setDeleteTxn(null)} className="flex-1 border-white/10">Cancel</Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
